import { getAllUsers } from "modules/systems/services/user.service";
import { subcontractApi } from "./subcontractApi";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const percentOf = (value, total) =>
  total > 0 ? Math.max(0, Math.min(100, (value / total) * 100)) : 0;

const latestBy = (items, fields) =>
  [...items].sort((left, right) => {
    const leftDate = fields.map((field) => left?.[field]).find(Boolean);
    const rightDate = fields.map((field) => right?.[field]).find(Boolean);
    return new Date(rightDate || 0) - new Date(leftDate || 0);
  })[0] || null;

const totalAfterTax = (contract) =>
  toNumber(contract?.totalValue) * (1 + toNumber(contract?.vat) / 100);

const profileAmountAfterTax = (profile, amountField, fallbackVat) => {
  const beforeTax = (profile?.items || []).reduce(
    (sum, item) => sum + toNumber(item?.[amountField]),
    0,
  );
  return beforeTax * (1 + toNumber(profile?.vat ?? fallbackVat) / 100);
};

export const summarizePayments = (contract, profiles = []) => {
  const paidAmount = profiles.reduce(
    (sum, profile) =>
      sum + profileAmountAfterTax(profile, "paymentAmount", contract?.vat),
    0,
  );
  const contractAmount = totalAfterTax(contract);
  const paidPercent = percentOf(paidAmount, contractAmount);

  return {
    ...contract,
    profiles,
    periodCount: profiles.length,
    paidAmount,
    remainingAmount: Math.max(contractAmount - paidAmount, 0),
    paidPercent,
    remainingPercent: Math.max(100 - paidPercent, 0),
    latestProfile: latestBy(profiles, ["paymentDate", "createdAt"]),
  };
};

export const summarizeAcceptances = (contract, profiles = []) => {
  const acceptedAmount = profiles.reduce(
    (sum, profile) =>
      sum + profileAmountAfterTax(profile, "acceptanceAmount", contract?.vat),
    0,
  );
  const contractAmount = totalAfterTax(contract);
  const acceptedPercent = percentOf(acceptedAmount, contractAmount);

  return {
    ...contract,
    profiles,
    periodCount: profiles.length,
    acceptedAmount,
    remainingAmount: Math.max(contractAmount - acceptedAmount, 0),
    acceptedPercent,
    remainingPercent: Math.max(100 - acceptedPercent, 0),
    latestProfile: latestBy(profiles, ["acceptanceDate", "createdAt"]),
  };
};

const userName = (user, fallback) =>
  user?.fullName ||
  user?.FullName ||
  user?.userName ||
  user?.UserName ||
  fallback;

export const summarizeApprovals = (contract, profiles = [], users = new Map()) => {
  const latestProfile = latestBy(profiles, ["submittedAt", "createdAt"]);

  return {
    ...contract,
    profiles,
    approvalCount: profiles.length,
    latestProfile,
    approvalName: latestProfile?.approvalName || "Chưa có hồ sơ phê duyệt",
    submittedAt: latestProfile?.submittedAt || latestProfile?.createdAt || null,
    submittedBy: userName(
      users.get(latestProfile?.submittedUserId),
      latestProfile?.submittedBy ||
        latestProfile?.createBy ||
        (latestProfile?.submittedUserId
          ? `Người dùng #${latestProfile.submittedUserId}`
          : "—"),
    ),
    approver: userName(
      users.get(latestProfile?.approverUserId),
      latestProfile?.approver ||
        (latestProfile?.approverUserId
          ? `Người dùng #${latestProfile.approverUserId}`
          : "—"),
    ),
    submitCount: toNumber(latestProfile?.submitCount),
    approvalStatus: latestProfile?.status || "Tạo mới",
  };
};

const mapWithConcurrency = async (items, limit, mapper) => {
  const results = new Array(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex++;
      results[currentIndex] = await mapper(items[currentIndex]);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );
  return results;
};

const loadContracts = async (options = {}) => {
  const { subcontractIds } = options;

  if (
    subcontractIds === "none" ||
    (Array.isArray(subcontractIds) && subcontractIds.length === 0)
  ) {
    return [];
  }

  const contractResult = await subcontractApi.getSubcontracts({
    pageSize: 10000,
    pageNumber: 1,
    contractIds:
      subcontractIds && subcontractIds !== "all" && subcontractIds.length > 0
        ? subcontractIds.join(",")
        : undefined,
  });
  return contractResult.data || [];
};

const loadSummaryList = async (type, options = {}) => {
  const contracts = await loadContracts(options);
  let users = new Map();

  if (type === "approvals") {
    try {
      const result = await getAllUsers();
      users = new Map(
        (result.data || []).map((user) => [user.userId ?? user.UserId, user]),
      );
    } catch {
      users = new Map();
    }
  }

  return mapWithConcurrency(contracts, 5, async (contract) => {
    try {
      if (type === "payments") {
        return summarizePayments(
          contract,
          await subcontractApi.getPaymentProfiles(contract.subcontractId),
        );
      }
      if (type === "acceptances") {
        return summarizeAcceptances(
          contract,
          await subcontractApi.getAcceptanceProfiles(contract.subcontractId),
        );
      }
      return summarizeApprovals(
        contract,
        await subcontractApi.getApprovalProfilesBySubcontract(contract.subcontractId),
        users,
      );
    } catch (error) {
      const empty =
        type === "payments"
          ? summarizePayments(contract)
          : type === "acceptances"
            ? summarizeAcceptances(contract)
            : summarizeApprovals(contract, [], users);
      return { ...empty, summaryError: error };
    }
  });
};

const loadAcceptanceApprovalSummaries = async (options = {}) => {
  const contracts = await loadContracts(options);

  return mapWithConcurrency(contracts, 5, async (contract) => {
    try {
      return {
        ...contract,
        acceptanceApprovalProfiles:
          await subcontractApi.getAcceptanceApprovalProfilesBySubcontract(
            contract.subcontractId,
          ),
      };
    } catch (error) {
      return {
        ...contract,
        acceptanceApprovalProfiles: [],
        acceptanceApprovalSummaryError: error,
      };
    }
  });
};

export const subcontractSummaryService = {
  getPaymentSummaries: (options) => loadSummaryList("payments", options),
  getAcceptanceSummaries: (options) => loadSummaryList("acceptances", options),
  getAcceptanceApprovalSummaries: (options) => loadAcceptanceApprovalSummaries(options),
  getApprovalSummaries: (options) => loadSummaryList("approvals", options),
};
